import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Quote, Star } from "lucide-react";
import { useLanguage } from "../i18n";

const CommunitySpotlight = () => {
  const { t, lang } = useLanguage();
  const [index, setIndex] = useState(0);
  const testimonials = t.community.testimonials;

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % 3), 5500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [lang]);

  return (
    <section
      id="community"
      data-testid="community-section"
      className="grain relative overflow-hidden border-t border-white/10 py-24 sm:py-32"
    >
      <div
        className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[480px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #00F5D4 0%, transparent 65%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.35em] text-[#00F5D4]">
            {t.community.eyebrow}
          </p>
          <h2 className="font-display mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t.community.title}
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-[#9E9E9E]">
            {t.community.sub}
          </p>
          <ul className="mt-8 space-y-4">
            {t.community.points.map((p, i) => (
              <motion.li
                key={`${lang}-point-${i}`}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                data-testid={`community-point-${i}`}
                className="flex items-start gap-3 text-sm text-[#F8F9FA]"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00F5D4]" />
                {p}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex flex-col justify-center"
        >
          <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.35em] text-[#6C757D]">
            {t.community.testimonialsLabel}
          </p>
          <div
            data-testid="testimonial-card"
            className="relative mt-5 min-h-[240px] border border-[#00F5D4]/25 bg-[#12141C] p-8"
          >
            <Quote className="h-8 w-8 text-[#00F5D4]/40" />
            <AnimatePresence mode="wait">
              <motion.figure
                key={`${lang}-${index}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.45 }}
                className="mt-4"
              >
                <blockquote
                  data-testid="testimonial-quote"
                  className="text-lg leading-relaxed text-[#F8F9FA]"
                >
                  “{testimonials[index].quote}”
                </blockquote>
                <figcaption className="mt-5">
                  <span className="font-display block text-sm font-bold text-white">
                    {testimonials[index].name}
                  </span>
                  <span className="font-mono-accent text-[11px] uppercase tracking-widest text-[#6C757D]">
                    {testimonials[index].role}
                  </span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
            <div className="absolute bottom-6 right-8 flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-6 bg-[#00F5D4]" : "w-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>

          <div
            data-testid="google-review-badge"
            className="mt-6 flex flex-col gap-4 border border-white/10 bg-[#12141C] p-6 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <div className="flex gap-1" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#FFD166] text-[#FFD166]" />
                ))}
              </div>
              <p className="font-display mt-2 text-base font-bold text-white">
                {t.community.reviewsTitle}
              </p>
              <p className="mt-1 text-sm text-[#9E9E9E]">{t.community.reviewsLine}</p>
            </div>
            <a
              href="https://maps.google.com/?q=Family+Auto+Sales+8670+Shannon+Rd+Shannon+NC+28386"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-button"
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[#FFD166]/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#FFD166] transition-colors duration-300 hover:bg-[#FFD166] hover:text-[#0A0B0E]"
            >
              {t.community.reviewsBtn}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CommunitySpotlight;
